<template>
  <div
    class="signature-canvas"
    :class="{
      'is-disabled': disabled,
      'is-virtual-landscape': virtualLandscape
    }"
  >
    <canvas ref="canvas" class="signature-canvas__surface" />
    <div v-if="isCanvasEmpty" class="signature-canvas__placeholder">请在签名区域签字</div>
  </div>
</template>

<script>
import SignaturePad from 'signature_pad'

export default {
  name: 'SignatureCanvas',
  props: {
    disabled: {
      type: Boolean,
      default: false
    },
    landscapeOnly: {
      type: Boolean,
      default: false
    },
    virtualLandscape: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      signaturePad: null,
      isCanvasEmpty: true,
      resizeTimer: null,
      canvasCssWidth: 0,
      canvasCssHeight: 0,
      isDrawing: false,
      resizePending: false,
      interactionStatePending: false
    }
  },
  watch: {
    disabled() {
      this.updateInteractionState()
    },
    virtualLandscape() {
      this.$nextTick(() => {
        this.refresh()
      })
    }
  },
  mounted() {
    this.initSignaturePad()
    window.addEventListener('resize', this.handleResize)
    window.addEventListener('orientationchange', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    window.removeEventListener('orientationchange', this.handleResize)
    clearTimeout(this.resizeTimer)
    if (this.signaturePad) {
      this.signaturePad.removeEventListener('beginStroke', this.handleBeginStroke)
      this.signaturePad.removeEventListener('endStroke', this.handleEndStroke)
      this.signaturePad.removeEventListener('beforeUpdateStroke', this.handleBeforeUpdateStroke)
      this.signaturePad.off()
    }
  },
  methods: {
    initSignaturePad() {
      this.signaturePad = new SignaturePad(this.$refs.canvas, {
        minWidth: 2,
        maxWidth: 5,
        penColor: '#2f5ba8',
        throttle: 8,
        velocityFilterWeight: 0.7
      })
      this.signaturePad.addEventListener('beginStroke', this.handleBeginStroke)
      this.signaturePad.addEventListener('endStroke', this.handleEndStroke)
      this.signaturePad.addEventListener('beforeUpdateStroke', this.handleBeforeUpdateStroke)
      this.resizeCanvas()
      this.updateInteractionState()
    },
    handleBeginStroke() {
      this.isDrawing = true
    },
    handleEndStroke() {
      this.isDrawing = false
      this.syncEmptyState()
      if (this.resizePending || this.interactionStatePending) {
        this.$nextTick(() => {
          if (this.resizePending) {
            this.resizePending = false
            this.resizeCanvas()
          }
          if (this.interactionStatePending) {
            this.interactionStatePending = false
            this.updateInteractionState()
          }
        })
      }
    },
    handleBeforeUpdateStroke(event) {
      if (!this.virtualLandscape || !event.detail || !this.$refs.canvas) {
        return
      }
      const canvas = this.$refs.canvas
      const rect = canvas.getBoundingClientRect()
      const logicalWidth = canvas.offsetWidth
      const logicalHeight = canvas.offsetHeight
      if (rect.width <= 0 || rect.height <= 0 || logicalWidth <= 0 || logicalHeight <= 0) {
        return
      }
      const visualX = event.detail.x - rect.left
      const visualY = event.detail.y - rect.top
      const logicalX = (visualY / rect.height) * logicalWidth
      const logicalY = ((rect.width - visualX) / rect.width) * logicalHeight
      event.detail.x = rect.left + logicalX
      event.detail.y = rect.top + logicalY
    },
    handleResize() {
      clearTimeout(this.resizeTimer)
      this.resizeTimer = setTimeout(() => {
        if (!this.isLandscapeReady()) {
          this.updateInteractionState()
          return
        }
        if (this.isDrawing) {
          this.resizePending = true
          return
        }
        this.resizeCanvas()
      }, 120)
    },
    resizeCanvas() {
      if (!this.signaturePad || !this.$refs.canvas) {
        return
      }
      if (this.isDrawing) {
        this.resizePending = true
        return
      }
      const pointGroups = this.signaturePad.isEmpty() ? [] : this.signaturePad.toData()
      const canvas = this.$refs.canvas
      const nextWidth = canvas.offsetWidth
      const nextHeight = canvas.offsetHeight
      if (nextWidth <= 0 || nextHeight <= 0) {
        return
      }
      const scaleX = this.canvasCssWidth > 0 ? nextWidth / this.canvasCssWidth : 1
      const scaleY = this.canvasCssHeight > 0 ? nextHeight / this.canvasCssHeight : 1
      const ratio = Math.max(window.devicePixelRatio || 1, 1)
      canvas.width = Math.floor(nextWidth * ratio)
      canvas.height = Math.floor(nextHeight * ratio)
      canvas.getContext('2d').scale(ratio, ratio)
      this.signaturePad.clear()
      if (pointGroups.length > 0) {
        this.signaturePad.fromData(this.scalePointGroups(pointGroups, scaleX, scaleY))
      }
      this.canvasCssWidth = nextWidth
      this.canvasCssHeight = nextHeight
      this.syncEmptyState()
    },
    scalePointGroups(pointGroups, scaleX, scaleY) {
      if (scaleX === 1 && scaleY === 1) {
        return pointGroups
      }
      return pointGroups.map(group => ({
        ...group,
        points: group.points.map(point => ({
          ...point,
          x: point.x * scaleX,
          y: point.y * scaleY
        }))
      }))
    },
    updateInteractionState() {
      if (!this.signaturePad) {
        return
      }
      if (this.isDrawing) {
        this.interactionStatePending = true
        return
      }
      this.interactionStatePending = false
      if (this.disabled || !this.isLandscapeReady()) {
        this.signaturePad.off()
        return
      }
      this.signaturePad.on()
    },
    isLandscapeReady() {
      return !this.landscapeOnly || this.virtualLandscape || this.isLandscapeViewport()
    },
    isLandscapeViewport() {
      return window.innerWidth > window.innerHeight
    },
    refresh() {
      if (!this.isLandscapeReady()) {
        this.updateInteractionState()
        return
      }
      this.resizeCanvas()
      this.updateInteractionState()
    },
    syncEmptyState() {
      this.isCanvasEmpty = !this.signaturePad || this.signaturePad.isEmpty()
      this.$emit('change', !this.isCanvasEmpty)
    },
    clear() {
      if (!this.signaturePad || this.disabled) {
        return
      }
      this.signaturePad.clear()
      this.syncEmptyState()
    },
    getSignatureBlob() {
      if (!this.signaturePad || this.signaturePad.isEmpty()) {
        return Promise.reject(new Error('请先完成签名'))
      }
      const trimmedCanvas = this.createTrimmedCanvas()
      return new Promise((resolve, reject) => {
        if (trimmedCanvas.toBlob) {
          trimmedCanvas.toBlob(blob => {
            if (blob) {
              resolve(blob)
              return
            }
            reject(new Error('签名图片生成失败'))
          }, 'image/png')
          return
        }
        try {
          resolve(this.dataUrlToBlob(trimmedCanvas.toDataURL('image/png')))
        } catch (error) {
          reject(error)
        }
      })
    },
    createTrimmedCanvas() {
      const sourceCanvas = this.$refs.canvas
      const sourceContext = sourceCanvas.getContext('2d')
      const width = sourceCanvas.width
      const height = sourceCanvas.height
      const pixels = sourceContext.getImageData(0, 0, width, height).data
      let minX = width
      let minY = height
      let maxX = 0
      let maxY = 0

      for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
          const alpha = pixels[(y * width + x) * 4 + 3]
          if (alpha === 0) {
            continue
          }
          minX = Math.min(minX, x)
          minY = Math.min(minY, y)
          maxX = Math.max(maxX, x)
          maxY = Math.max(maxY, y)
        }
      }

      const ratio = Math.max(window.devicePixelRatio || 1, 1)
      const padding = Math.round(18 * ratio)
      const startX = Math.max(minX - padding, 0)
      const startY = Math.max(minY - padding, 0)
      const endX = Math.min(maxX + padding, width)
      const endY = Math.min(maxY + padding, height)
      const targetWidth = Math.max(endX - startX, 1)
      const targetHeight = Math.max(endY - startY, 1)
      const targetCanvas = document.createElement('canvas')
      targetCanvas.width = targetWidth
      targetCanvas.height = targetHeight
      targetCanvas.getContext('2d').drawImage(
        sourceCanvas,
        startX,
        startY,
        targetWidth,
        targetHeight,
        0,
        0,
        targetWidth,
        targetHeight
      )
      return targetCanvas
    },
    dataUrlToBlob(dataUrl) {
      const parts = dataUrl.split(',')
      const mime = parts[0].match(/:(.*?);/)[1]
      const binary = window.atob(parts[1])
      const bytes = new Uint8Array(binary.length)
      for (let index = 0; index < binary.length; index += 1) {
        bytes[index] = binary.charCodeAt(index)
      }
      return new Blob([bytes], { type: mime })
    }
  }
}
</script>

<style scoped lang="scss">
.signature-canvas {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: #fbfdff;
  border: 1px dashed #bfd1e6;
  border-radius: 4px;
  touch-action: none;

  &.is-disabled {
    background: #f7f9fb;
  }
}

.signature-canvas__surface {
  display: block;
  width: 100%;
  height: 100%;
  touch-action: none;
}

.signature-canvas__placeholder {
  position: absolute;
  top: 50%;
  left: 50%;
  color: #d4d8de;
  font-size: 16px;
  letter-spacing: 2px;
  writing-mode: vertical-rl;
  transform: translate(-50%, -50%);
  pointer-events: none;
}

@media screen and (orientation: landscape) {
  .signature-canvas__placeholder {
    writing-mode: horizontal-tb;
  }
}

.signature-canvas.is-virtual-landscape .signature-canvas__placeholder {
  writing-mode: horizontal-tb;
}
</style>
